import { useEffect, useRef, type ReactNode } from "react";
import * as THREE from "three";

interface DitherPrismHeroProps {
  color1?: string;
  color2?: string;
  color3?: string;
  speed?: number;
  ditherIntensity?: number;
  prismIntensity?: number;
  particleCount?: number;
  showParticles?: boolean;
  particleColor?: string;
  className?: string;
  children?: ReactNode;
}

const VERTEX = `varying vec2 vUv;varying vec3 vPosition;
void main(){vUv=uv;vPosition=position;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`;

const FRAGMENT = `uniform float uTime;uniform vec2 uResolution;uniform vec2 uMouse;uniform float uMouseIntensity;
uniform vec3 uColor1;uniform vec3 uColor2;uniform vec3 uColor3;
uniform float uDitherIntensity;uniform float uPrismIntensity;
varying vec2 vUv;varying vec3 vPosition;
float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
vec3 permute(vec3 x){return mod(((x*34.0)+1.0)*x,289.0);}
float snoise(vec2 v){
  const vec4 C=vec4(0.211324865405187,0.366025403784439,-0.577350269189626,0.024390243902439);
  vec2 i=floor(v+dot(v,C.yy));vec2 x0=v-i+dot(i,C.xx);
  vec2 i1=(x0.x>x0.y)?vec2(1.0,0.0):vec2(0.0,1.0);
  vec4 x12=x0.xyxy+C.xxzz;x12.xy-=i1;i=mod(i,289.0);
  vec3 p=permute(permute(i.y+vec3(0.0,i1.y,1.0))+i.x+vec3(0.0,i1.x,1.0));
  vec3 m=max(0.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.0);
  m=m*m;m=m*m;
  vec3 x2=2.0*fract(p*C.www)-1.0;vec3 h=abs(x2)-0.5;
  vec3 ox=floor(x2+0.5);vec3 a0=x2-ox;
  m*=1.79284291400159-0.85373472095314*(a0*a0+h*h);
  vec3 g;g.x=a0.x*x0.x+h.x*x0.y;g.yz=a0.yz*x12.xz+h.yz*x12.yw;
  return 130.0*dot(m,g);
}
float fbm(vec2 p,int octaves){
  float value=0.0;float amplitude=0.5;float frequency=1.0;
  for(int i=0;i<6;i++){if(i>=octaves)break;value+=amplitude*snoise(p*frequency);frequency*=2.0;amplitude*=0.5;}
  return value;
}
float bayer8x8(vec2 uv){
  ivec2 p=ivec2(mod(uv,8.0));
  int matrix[64];
  matrix[0]=0;matrix[1]=32;matrix[2]=8;matrix[3]=40;matrix[4]=2;matrix[5]=34;matrix[6]=10;matrix[7]=42;
  matrix[8]=48;matrix[9]=16;matrix[10]=56;matrix[11]=24;matrix[12]=50;matrix[13]=18;matrix[14]=58;matrix[15]=26;
  matrix[16]=12;matrix[17]=44;matrix[18]=4;matrix[19]=36;matrix[20]=14;matrix[21]=46;matrix[22]=6;matrix[23]=38;
  matrix[24]=60;matrix[25]=28;matrix[26]=52;matrix[27]=20;matrix[28]=62;matrix[29]=30;matrix[30]=54;matrix[31]=22;
  matrix[32]=3;matrix[33]=35;matrix[34]=11;matrix[35]=43;matrix[36]=1;matrix[37]=33;matrix[38]=9;matrix[39]=41;
  matrix[40]=51;matrix[41]=19;matrix[42]=59;matrix[43]=27;matrix[44]=49;matrix[45]=17;matrix[46]=57;matrix[47]=25;
  matrix[48]=15;matrix[49]=47;matrix[50]=7;matrix[51]=39;matrix[52]=13;matrix[53]=45;matrix[54]=5;matrix[55]=37;
  matrix[56]=63;matrix[57]=31;matrix[58]=55;matrix[59]=23;matrix[60]=61;matrix[61]=29;matrix[62]=53;matrix[63]=21;
  return float(matrix[p.y*8+p.x])/64.0;
}
float blueNoise(vec2 uv,float time){
  float n1=hash(uv+vec2(time*0.1,0.0));float n2=hash(uv*2.1+vec2(0.0,time*0.13));
  float n3=hash(uv*4.3+vec2(time*0.07,time*0.11));return fract(n1+n2*0.5+n3*0.25);
}
vec3 prism(vec2 uv,float time,float intensity){
  float angle=atan(uv.y-0.5,uv.x-0.5);float dist=length(uv-0.5);
  float prismAngle=angle+time*0.3+dist*3.0;
  float r=0.5+0.5*sin(prismAngle);float g=0.5+0.5*sin(prismAngle+2.094);float b=0.5+0.5*sin(prismAngle+4.188);
  return vec3(r,g,b)*intensity;
}
vec3 iridescence(vec2 uv,float time){
  float t=time*0.5;vec2 p=uv*3.0;
  float n1=snoise(p+vec2(t,0.0));float n2=snoise(p*1.3+vec2(0.0,t*0.7));float n3=snoise(p*0.7+vec2(t*0.5,t*0.3));
  vec3 col1=vec3(0.5+0.5*sin(n1*3.14159+t));vec3 col2=vec3(0.5+0.5*sin(n2*3.14159+t*1.3+2.0));
  vec3 col3=vec3(0.5+0.5*sin(n3*3.14159+t*0.7+4.0));return(col1+col2+col3)/3.0;
}
float diamond(vec2 p){return abs(p.x)+abs(p.y);}
float morphShape(vec2 uv,float time){
  float morph=sin(time*0.4)*0.5+0.5;vec2 p=uv*4.0-2.0;p=p+vec2(sin(time*0.3),cos(time*0.4))*0.5;
  float circle=length(p)-1.0;float diam=diamond(p)-1.4;float shape=mix(circle,diam,morph);
  vec2 q=mod(uv*8.0,2.0)-1.0;float multiShape=mix(length(q),diamond(q),morph)-0.3;return min(shape,multiShape);
}
float mouseRipple(vec2 uv,vec2 mouse,float time,float intensity){
  vec2 d=uv-mouse;float dist=length(d);
  // organic liquid distortion driven by low-freq noise around cursor
  float n1=snoise(d*3.5+vec2(time*0.35,-time*0.25));
  float n2=snoise(d*6.0+vec2(-time*0.2,time*0.4));
  float blob=(n1*0.7+n2*0.3)*exp(-dist*2.5);
  return blob*intensity*1.2;
}
vec3 mouseGlow(vec2 uv,vec2 mouse,float time,float intensity,vec3 glowColor){
  vec2 d=uv-mouse;
  // warp the cursor position with noise so the glow feels like a fluid blob
  float wx=snoise(d*4.0+vec2(time*0.4,0.0))*0.06;
  float wy=snoise(d*4.0+vec2(0.0,time*0.35))*0.06;
  float dist=length(d+vec2(wx,wy));
  float soft=exp(-dist*4.0)*0.9;
  float pulse=0.85+0.15*sin(time*1.5);
  return glowColor*soft*pulse*intensity;
}
vec2 mouseLensDistort(vec2 uv,vec2 mouse,float intensity){
  vec2 delta=uv-mouse;float dist=length(delta);
  // add swirl + noise offset for a liquid, non-radial feel
  float ang=atan(delta.y,delta.x)+snoise(delta*3.0+vec2(0.0,0.0))*0.8;
  vec2 dir=vec2(cos(ang),sin(ang));
  float distortion=exp(-dist*4.5)*intensity*0.18;
  return uv+dir*distortion;
}
void main(){
  vec2 uv=vUv;vec2 pixelCoord=gl_FragCoord.xy;float time=uTime;
  vec2 distortedUv=mouseLensDistort(uv,uMouse,uMouseIntensity);
  float noise1=fbm(distortedUv*2.0+vec2(time*0.05,time*0.03),4);
  float noise2=fbm(distortedUv*3.0+vec2(-time*0.04,time*0.06),3);
  float diagonal=(distortedUv.x+distortedUv.y)*0.5;
  float flow=diagonal+noise1*0.3+noise2*0.2;flow+=sin(time*0.2)*0.1;
  vec3 col;float t1=smoothstep(0.0,0.5,flow);float t2=smoothstep(0.5,1.0,flow);
  col=mix(uColor1,uColor2,t1);col=mix(col,uColor3,t2);
  vec3 prismColor=prism(distortedUv,time,uPrismIntensity);
  float edgeMask=abs(fract(flow*5.0)-0.5)*2.0;edgeMask=smoothstep(0.3,0.7,edgeMask);
  col+=prismColor*edgeMask*0.4;
  vec3 iris=iridescence(distortedUv,time);
  float irisMask=snoise(distortedUv*5.0+time*0.1);irisMask=smoothstep(-0.2,0.8,irisMask)*0.15;
  col=mix(col,iris,irisMask);
  float shape=morphShape(distortedUv,time);float shapeMask=1.0-smoothstep(-0.1,0.1,shape);
  col=mix(col,col*1.15+vec3(0.08),shapeMask*0.3);
  float ripple=mouseRipple(uv,uMouse,time,uMouseIntensity);
  col+=ripple*prismColor*0.6;col+=ripple*vec3(0.15,0.1,0.22);
  vec3 glow=mouseGlow(uv,uMouse,time,uMouseIntensity,vec3(0.55,0.45,0.75));col+=glow*0.35;
  float mouseDist=length(uv-uMouse);float proximityBoost=exp(-mouseDist*4.0)*uMouseIntensity;
  col=mix(col,col*1.5+prismColor*0.3,proximityBoost);
  float bayer=bayer8x8(pixelCoord);float blue=blueNoise(pixelCoord*0.1,time);
  float ditherPattern=mix(bayer,blue,0.3+0.2*sin(time*0.5));
  vec3 ditherOffset=(vec3(ditherPattern)-0.5)*uDitherIntensity;col+=ditherOffset;
  float levels=16.0;vec3 quantized=floor(col*levels+ditherPattern)/levels;
  col=mix(col,quantized,uDitherIntensity*0.5);
  float scanline=sin(pixelCoord.y*2.0+time*2.0)*0.02;col+=scanline*uDitherIntensity;
  float vignette=1.0-length((uv-0.5)*1.2);vignette=smoothstep(0.0,0.7,vignette);
  col*=0.85+vignette*0.15;
  col=clamp(col,0.0,1.0);gl_FragColor=vec4(col,1.0);
}`;

export default function DitherPrismHero({
  color1 = "#0f0f23",
  color2 = "#6366f1",
  color3 = "#ec4899",
  speed = 1,
  ditherIntensity = 0.15,
  prismIntensity = 0.5,
  particleCount = 50,
  showParticles = true,
  particleColor = "#ffffff",
  className = "",
  children,
}: DitherPrismHeroProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5, intensity: 0.15 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const { width, height } = container.getBoundingClientRect();

    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      alpha: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 10);
    camera.position.z = 1;

    const planeGeo = new THREE.PlaneGeometry(2, 2);
    const planeMat = new THREE.ShaderMaterial({
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      uniforms: {
        uTime: { value: 0 },
        uResolution: { value: new THREE.Vector2(width, height) },
        uMouse: { value: new THREE.Vector2(0.5, 0.5) },
        uMouseIntensity: { value: 0.15 },
        uColor1: { value: new THREE.Color(color1) },
        uColor2: { value: new THREE.Color(color2) },
        uColor3: { value: new THREE.Color(color3) },
        uDitherIntensity: { value: ditherIntensity },
        uPrismIntensity: { value: prismIntensity },
      },
      transparent: true,
      depthWrite: false,
      depthTest: false,
    });
    const planeMesh = new THREE.Mesh(planeGeo, planeMat);
    planeMesh.scale.set(2, 2, 1);
    scene.add(planeMesh);

    // Particles
    let pointsGeo: THREE.BufferGeometry | null = null;
    let pointsMat: THREE.PointsMaterial | null = null;
    let particlePhases: Float32Array | null = null;

    if (showParticles) {
      const count = particleCount;
      const positions = new Float32Array(count * 3);
      particlePhases = new Float32Array(count);
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (Math.random() - 0.5) * 4;
        positions[i * 3 + 1] = (Math.random() - 0.5) * 4;
        positions[i * 3 + 2] = (Math.random() - 0.5) * 2;
        particlePhases[i] = Math.random() * Math.PI * 2;
      }
      pointsGeo = new THREE.BufferGeometry();
      pointsGeo.setAttribute(
        "position",
        new THREE.BufferAttribute(positions, 3),
      );
      pointsMat = new THREE.PointsMaterial({
        color: new THREE.Color(particleColor),
        size: 0.02,
        transparent: true,
        opacity: 0.6,
        sizeAttenuation: true,
        blending: THREE.AdditiveBlending,
      });
      const points = new THREE.Points(pointsGeo, pointsMat);
      scene.add(points);
    }

    const handleResize = () => {
      if (!container) return;
      const rect = container.getBoundingClientRect();
      renderer.setSize(rect.width, rect.height);
      camera.aspect = rect.width / rect.height;
      camera.updateProjectionMatrix();
      planeMat.uniforms.uResolution.value.set(rect.width, rect.height);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      mouseRef.current.x = (e.clientX - rect.left) / rect.width;
      mouseRef.current.y = 1 - (e.clientY - rect.top) / rect.height;
      mouseRef.current.intensity = 0.35;
    };

    const handleMouseLeave = () => {
      mouseRef.current.intensity = 0.1;
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);
    window.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseleave", handleMouseLeave);

    const clock = new THREE.Clock();
    let rafId = 0;

    const loop = () => {
      const t = clock.getElapsedTime() * speed;
      planeMat.uniforms.uTime.value = t;
      planeMat.uniforms.uMouse.value.set(
        mouseRef.current.x,
        mouseRef.current.y,
      );
      // Smoothly ease intensity
      const target = mouseRef.current.intensity;
      const current = planeMat.uniforms.uMouseIntensity.value as number;
      planeMat.uniforms.uMouseIntensity.value =
        current + (target - current) * 0.08;

      if (pointsGeo && particlePhases) {
        const posAttr = pointsGeo.getAttribute("position");
        if (posAttr) {
          const positions = posAttr.array as Float32Array;
          for (let i = 0; i < particleCount; i++) {
            const phase = particlePhases[i] ?? 0;
            const yIdx = i * 3 + 1;
            const xIdx = i * 3;
            positions[yIdx] =
              (positions[yIdx] ?? 0) + Math.sin(t + phase) * 0.001;
            positions[xIdx] =
              (positions[xIdx] ?? 0) + Math.cos(t * 0.5 + phase) * 0.0005;
            if ((positions[yIdx] ?? 0) > 2) positions[yIdx] = -2;
            if ((positions[yIdx] ?? 0) < -2) positions[yIdx] = 2;
          }
          posAttr.needsUpdate = true;
        }
      }

      renderer.render(scene, camera);
      rafId = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("mouseleave", handleMouseLeave);
      planeGeo.dispose();
      planeMat.dispose();
      pointsGeo?.dispose();
      pointsMat?.dispose();
      renderer.dispose();
    };
  }, [
    color1,
    color2,
    color3,
    speed,
    ditherIntensity,
    prismIntensity,
    particleCount,
    showParticles,
    particleColor,
  ]);

  return (
    <div
      ref={containerRef}
      className={`relative flex min-h-screen w-full flex-col items-center overflow-hidden text-gray-900 ${className}`}
    >
      <canvas
        ref={canvasRef}
        className="pointer-events-none absolute inset-0 z-0 h-full w-full"
      />
      {children ? (
        <div className="relative z-10 flex w-full flex-1 flex-col">
          {children}
        </div>
      ) : null}
    </div>
  );
}